import { useEffect } from 'react';

interface PageMetaOptions {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  keywords?: string;
  canonical?: string;
  noindex?: boolean;
  nofollow?: boolean;
}

export function usePageMeta(options: PageMetaOptions) {
  useEffect(() => {
    if (options.title) {
      document.title = options.title;
    }

    // Update meta tags
    const updateMetaTag = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = name;
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    const updatePropertyTag = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    if (options.description) {
      updateMetaTag('description', options.description);
      updatePropertyTag('og:description', options.description);
      updateMetaTag('twitter:description', options.description);
    }

    if (options.image) {
      updatePropertyTag('og:image', options.image);
      updateMetaTag('twitter:image', options.image);
    }

    if (options.url) {
      updatePropertyTag('og:url', options.url);
    }

    if (options.keywords) {
      updateMetaTag('keywords', options.keywords);
    }

    if (options.title) {
      updatePropertyTag('og:title', options.title);
      updateMetaTag('twitter:title', options.title);
    }

    if (options.noindex) {
      updateMetaTag('robots', options.nofollow ? 'noindex, nofollow' : 'noindex');
    }

    // If canonical is provided, use it. Otherwise, use plain origin + pathname without query params
    const canonicalUrl = options.canonical || (typeof window !== 'undefined' ? (window.location.origin + window.location.pathname) : '');

    if (canonicalUrl) {
      // Ensure the canonical URL never has query parameters or trailing slashes unless explicitly desired (which would be in options.canonical)
      // Since we build the default one from origin + pathname, it won't have query params.
      // But we should also make sure we deal with trailing slashes consistently if needed (usually prefer without, or strict match).
      // For this user: we'll just set it.
      
      let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      canonical.href = canonicalUrl;
    }
  }, [options]);
}

export function injectJsonLd(data: any): () => void {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);

  // Return cleanup function
  return () => {
    if (script.parentNode) {
      script.parentNode.removeChild(script);
    }
  };
}